import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../Context/AuthContext';
import { API_ENDPOINTS } from '../../config/api';
import CalendarEditModal from './CalendarEditModal';
import './CustomCalendar.css';

const CustomCalendar = () => {
    const { familyMembers, loading: authLoading } = useAuth();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [currentView, setCurrentView] = useState('agenda');
    const [currentDate, setCurrentDate] = useState(new Date());
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [draggedEvent, setDraggedEvent] = useState(null);

    // Fetch calendar events
    const fetchEvents = useCallback(async () => {
        try {
            setLoading(true);
            setError(''); // Clear previous errors
            console.log('Fetching calendar data from:', `${API_ENDPOINTS.CALENDAR}/getData`);

            const response = await fetch(`${API_ENDPOINTS.CALENDAR}/getData`);
            console.log('Response status:', response.status);
            console.log('Response headers:', response.headers);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('API Error Response:', errorText);
                throw new Error(`Failed to fetch calendar data: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();
            console.log('Calendar data received:', data);
            setEvents(data);
        } catch (err) {
            console.error('Error fetching calendar data:', err);
            setError(`Failed to load calendar data: ${err.message}`);
        } finally {
            setLoading(false);
        }
    }, []);

    // Load events on component mount
    useEffect(() => {
        fetchEvents();
    }, [fetchEvents]);

    // Handle event creation/update/deletion
    const handleEventSave = async (eventId, eventData, isDelete = false) => {
        try {
            let response;

            if (isDelete) {
                // Delete event
                response = await fetch(`${API_ENDPOINTS.CALENDAR}/${eventId}`, {
                    method: 'DELETE'
                });
            } else if (eventId) {
                // Update existing event
                const updateData = { ...eventData };
                // Remove id from updateData to avoid database column mismatch
                delete updateData.id;
                delete updateData.Id;
                response = await fetch(`${API_ENDPOINTS.CALENDAR}/${eventId}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(updateData)
                });
            } else {
                // Create new event
                response = await fetch(`${API_ENDPOINTS.CALENDAR}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(eventData)
                });
            }

            if (!response.ok) {
                const errorText = await response.text();
                console.error('API Error:', errorText);
                throw new Error(`Failed to ${isDelete ? 'delete' : 'save'} event: ${response.status}`);
            }

            await fetchEvents(); // Refresh events
            setIsModalOpen(false);
            setSelectedEvent(null);
        } catch (err) {
            console.error('Error saving event:', err);
            setError(`Failed to ${isDelete ? 'delete' : 'save'} event: ${err.message}`);
        }
    };

    // Handle event deletion
    const handleEventDelete = async (eventId) => {
        try {
            const response = await fetch(`${API_ENDPOINTS.CALENDAR}/${eventId}`, {
                method: 'DELETE'
            });

            if (!response.ok) {
                throw new Error('Failed to delete event');
            }

            await fetchEvents(); // Refresh events
        } catch (err) {
            console.error('Error deleting event:', err);
            setError('Failed to delete event');
        }
    };

    // Handle event click
    const handleEventClick = (event) => {
        setSelectedEvent(event);
        setIsModalOpen(true);
    };

    // Handle cell click for new events
    const handleCellClick = (date, time = null) => {
        const newEvent = {
            StartTime: time ? new Date(`${date.toISOString().split('T')[0]}T${time}:00`) : new Date(date),
            EndTime: time ? new Date(new Date(`${date.toISOString().split('T')[0]}T${time}:00`).getTime() + 30 * 60 * 1000) : new Date(new Date(date).getTime() + 30 * 60 * 1000),
            Subject: '',
            Description: '',
            Location: '',
            FamilyId: 1,
            IsAllDay: false
        };
        setSelectedEvent(newEvent);
        setIsModalOpen(true);
    };

    // Handle drag start
    const handleDragStart = (event, e) => {
        e.preventDefault();
        setDraggedEvent(event);
    };

    // Handle drag over
    const handleDragOver = (e) => {
        e.preventDefault();
    };

    // Handle drop
    const handleDrop = async (targetDate, targetTime, e) => {
        e.preventDefault();
        if (!draggedEvent) return;

        try {
            const newStartTime = targetTime ?
                new Date(`${targetDate.toISOString().split('T')[0]}T${targetTime}:00`) :
                new Date(targetDate);

            const duration = new Date(draggedEvent.EndTime).getTime() - new Date(draggedEvent.StartTime).getTime();
            const newEndTime = new Date(newStartTime.getTime() + duration);

            const updatedEvent = {
                ...draggedEvent,
                StartTime: newStartTime.toISOString(),
                EndTime: newEndTime.toISOString()
            };

            await handleEventSave(updatedEvent.id, updatedEvent);
            setDraggedEvent(null);
        } catch (err) {
            console.error('Error moving event:', err);
            setError('Failed to move event');
        }
    };

    // Get time string helper - consistent with appointment screen
    const getTimeString = (dateTime) => {
        if (!dateTime) return '';
        const date = new Date(dateTime);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    // Get family member by ID
    const getFamilyMember = (familyId) => {
        // Handle both single ID and array of IDs
        const id = Array.isArray(familyId) ? familyId[0] : familyId;
        return familyMembers.find(f => f.id === id) || { name: `Family ${id}`, color: '#007bff' };
    };

    // Sort events chronologically
    const sortEventsChronologically = (eventsList) => {
        return [...eventsList].sort((a, b) => new Date(a.StartTime) - new Date(b.StartTime));
    };

    // Filter events by date range (for future use)
    // const filterEventsByDateRange = (eventsList, startDate, endDate) => {
    //     return eventsList.filter(event => {
    //         const eventStart = new Date(event.StartTime);
    //         const eventEnd = new Date(event.EndTime);
    //         return (eventStart >= startDate && eventStart <= endDate) ||
    //                (eventEnd >= startDate && eventEnd <= endDate) ||
    //                (eventStart <= startDate && eventEnd >= endDate);
    //     });
    // };

    // Check if event is multi-day
    const isMultiDayEvent = (event) => {
        const start = new Date(event.StartTime);
        const end = new Date(event.EndTime);
        return start.toDateString() !== end.toDateString();
    };

    // Check if event is all-day
    const isAllDayEvent = (event) => {
        return event.IsAllDay ||
            (new Date(event.StartTime).getHours() === 0 &&
                new Date(event.EndTime).getHours() === 23);
    };

    // Navigation functions
    const goToPrevious = () => {
        const newDate = new Date(currentDate);
        if (currentView === 'month') {
            newDate.setMonth(newDate.getMonth() - 1);
        } else if (currentView === 'week') {
            newDate.setDate(newDate.getDate() - 7);
        } else {
            newDate.setDate(newDate.getDate() - 1);
        }
        setCurrentDate(newDate);
    };

    const goToNext = () => {
        const newDate = new Date(currentDate);
        if (currentView === 'month') {
            newDate.setMonth(newDate.getMonth() + 1);
        } else if (currentView === 'week') {
            newDate.setDate(newDate.getDate() + 7);
        } else {
            newDate.setDate(newDate.getDate() + 1);
        }
        setCurrentDate(newDate);
    };

    const goToToday = () => {
        setCurrentDate(new Date());
    };

    // Loading state
    if (loading || authLoading) {
        return (
            <div className="custom-calendar-loading">
                <div className="loading-spinner"></div>
                <p>Loading calendar...</p>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="custom-calendar-error">
                <p>Error: {error}</p>
                <button onClick={fetchEvents}>Retry</button>
            </div>
        );
    }

    return (
        <div className="custom-calendar">
            {/* Header with navigation and view switcher */}
            <div className="calendar-header">
                <div className="calendar-navigation">
                    <button onClick={goToPrevious} className="nav-button">
                        ←
                    </button>
                    <button onClick={goToToday} className="today-button">
                        Today
                    </button>
                    <button onClick={goToNext} className="nav-button">
                        →
                    </button>
                </div>

                <div className="calendar-title">
                    {currentView === 'agenda' && 'Agenda'}
                    {currentView === 'week' && `Week of ${currentDate.toLocaleDateString()}`}
                    {currentView === 'month' && currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </div>

                <div className="view-switcher">
                    <button
                        className={currentView === 'agenda' ? 'active' : ''}
                        onClick={() => setCurrentView('agenda')}
                    >
                        Agenda
                    </button>
                    <button
                        className={currentView === 'week' ? 'active' : ''}
                        onClick={() => setCurrentView('week')}
                    >
                        Week
                    </button>
                    <button
                        className={currentView === 'month' ? 'active' : ''}
                        onClick={() => setCurrentView('month')}
                    >
                        Month
                    </button>
                </div>
            </div>

            {/* Calendar content */}
            <div className="calendar-content">
                {currentView === 'agenda' && (
                    <AgendaView
                        events={sortEventsChronologically(events)}
                        onEventClick={handleEventClick}
                        onEventDelete={handleEventDelete}
                        getFamilyMember={getFamilyMember}
                        getTimeString={getTimeString}
                        currentDate={currentDate}
                        onCellClick={handleCellClick}
                    />
                )}

                {currentView === 'week' && (
                    <WeekView
                        events={events}
                        currentDate={currentDate}
                        onEventClick={handleEventClick}
                        onCellClick={handleCellClick}
                        onDragStart={handleDragStart}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        getFamilyMember={getFamilyMember}
                        getTimeString={getTimeString}
                        isMultiDayEvent={isMultiDayEvent}
                        isAllDayEvent={isAllDayEvent}
                    />
                )}

                {currentView === 'month' && (
                    <MonthView
                        events={events}
                        currentDate={currentDate}
                        onEventClick={handleEventClick}
                        onCellClick={handleCellClick}
                        getFamilyMember={getFamilyMember}
                        getTimeString={getTimeString}
                    />
                )}
            </div>

            {/* Event edit modal */}
            <CalendarEditModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setSelectedEvent(null);
                }}
                event={selectedEvent}
                onSave={handleEventSave}
                resourceDataSource={familyMembers.map(member => ({
                    Name: member.name,
                    Id: member.id,
                    Color: member.color
                }))}
            />
        </div>
    );
};

// Agenda View Component
const AgendaView = ({ events, onEventClick, onEventDelete, getFamilyMember, getTimeString, currentDate, onCellClick }) => {
    // Filter events to show events for the current date and surrounding days
    const startDate = new Date(currentDate);
    startDate.setDate(currentDate.getDate() - 3); // Show 3 days before
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date(currentDate);
    endDate.setDate(currentDate.getDate() + 3); // Show 3 days after
    endDate.setHours(23, 59, 59, 999);

    const filteredEvents = events.filter(event => {
        const eventStart = new Date(event.StartTime);
        const eventEnd = new Date(event.EndTime);

        // Check if event overlaps with the date range
        return (eventStart <= endDate && eventEnd >= startDate);
    });

    // Sort events chronologically
    const sortedEvents = filteredEvents.sort((a, b) => new Date(a.StartTime) - new Date(b.StartTime));

    return (
        <div className="agenda-view">
            <div className="agenda-header">
                <p>{startDate.toLocaleDateString()} - {endDate.toLocaleDateString()}</p>
                <button
                    className="add-event-button"
                    onClick={() => onCellClick(currentDate, '09:00')}
                >
                    + Add Event
                </button>
            </div>
            {sortedEvents.length === 0 ? (
                <div className="no-events">
                    <p>No events scheduled in this date range</p>
                    <button
                        className="add-event-button"
                        onClick={() => onCellClick(currentDate, '09:00')}
                    >
                        + Add Event
                    </button>
                </div>
            ) : (
                <div className="agenda-events">
                    {sortedEvents.map((event) => {
                        const family = getFamilyMember(event.FamilyId);
                        const eventDate = new Date(event.StartTime);
                        return (
                            <div
                                key={event.id}
                                className="agenda-event"
                                onClick={() => onEventClick(event)}
                                style={{
                                    borderLeft: `4px solid ${family.color}`,
                                    background: `linear-gradient(135deg, ${family.color}15 0%, ${family.color}25 100%)`
                                }}
                            >
                                <div className="event-time">
                                    {eventDate.toLocaleDateString()} - {getTimeString(event.StartTime)} - {getTimeString(event.EndTime)}
                                </div>
                                <div className="event-subject">{event.Subject}</div>
                                <div className="event-family">{family.name}</div>
                                {event.Description && (
                                    <div className="event-description">{event.Description}</div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// Week View Component
const WeekView = ({
    events,
    currentDate,
    onEventClick,
    onCellClick,
    onDragStart,
    onDragOver,
    onDrop,
    getFamilyMember,
    getTimeString,
    isMultiDayEvent,
    isAllDayEvent
}) => {
    // Get week dates
    const getWeekDates = (date) => {
        const week = [];
        const startOfWeek = new Date(date);
        const day = startOfWeek.getDay();
        const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1); // Monday start
        startOfWeek.setDate(diff);

        for (let i = 0; i < 7; i++) {
            const dayDate = new Date(startOfWeek);
            dayDate.setDate(startOfWeek.getDate() + i);
            week.push(dayDate);
        }
        return week;
    };

    const weekDates = getWeekDates(currentDate);

    // Define 4 time periods instead of 24 hourly slots
    const timePeriods = [
        { label: 'Early Morning', start: 1, end: 8, hours: [1, 2, 3, 4, 5, 6, 7] },
        { label: 'Morning', start: 8, end: 12, hours: [8, 9, 10, 11] },
        { label: 'Afternoon', start: 12, end: 18, hours: [12, 13, 14, 15, 16, 17] },
        { label: 'Evening', start: 18, end: 24, hours: [18, 19, 20, 21, 22, 23] }
    ];

    return (
        <div className="week-view">
            <div className="week-header">
                <div className="time-column"></div>
                {weekDates.map((date, index) => (
                    <div key={index} className="day-header">
                        <div className="day-name">
                            {date.toLocaleDateString('en-US', { weekday: 'short' })}
                        </div>
                        <div className="day-number">
                            {date.getDate()}
                        </div>
                    </div>
                ))}
            </div>

            <div className="week-content">
                {/* All-day section - spans all columns */}
                <div className="all-day-row">
                    <div className="time-column-header">
                        All Day
                    </div>
                    {weekDates.map((date, dayIndex) => {
                        const dayEvents = events.filter(event => {
                            const eventStart = new Date(event.StartTime);
                            const eventEnd = new Date(event.EndTime);
                            const dayStart = new Date(date);
                            dayStart.setHours(0, 0, 0, 0);
                            const dayEnd = new Date(date);
                            dayEnd.setHours(23, 59, 59, 999);

                            // Check if event overlaps with this day
                            return (eventStart <= dayEnd && eventEnd >= dayStart);
                        });

                        const allDayEvents = dayEvents.filter(isAllDayEvent);

                        return (
                            <div key={dayIndex} className="all-day-column">
                                {allDayEvents.map((event) => {
                                    const family = getFamilyMember(event.FamilyId);
                                    return (
                                        <div
                                            key={event.id}
                                            className="all-day-event"
                                            onClick={() => onEventClick(event)}
                                            style={{
                                                background: `linear-gradient(135deg, ${family.color}15 0%, ${family.color}25 100%)`,
                                                border: `2px solid ${family.color}`
                                            }}
                                        >
                                            <div className="event-subject">{event.Subject}</div>
                                            <div className="event-time">{date.toLocaleDateString()}</div>
                                            <div className="event-family">{family.name}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>

                {/* Time periods section - spans all columns */}
                <div className="time-periods-row">
                    <div className="time-column">
                        {timePeriods.map((period, index) => (
                            <div key={index} className="time-period">
                                <div className="period-label">{period.label}</div>
                                <div className="period-time">{period.start}:00 - {period.end}:00</div>
                            </div>
                        ))}
                    </div>

                    {weekDates.map((date, dayIndex) => {
                        const dayEvents = events.filter(event => {
                            const eventStart = new Date(event.StartTime);
                            const eventEnd = new Date(event.EndTime);
                            const dayStart = new Date(date);
                            dayStart.setHours(0, 0, 0, 0);
                            const dayEnd = new Date(date);
                            dayEnd.setHours(23, 59, 59, 999);

                            // Check if event overlaps with this day
                            return (eventStart <= dayEnd && eventEnd >= dayStart);
                        });

                        const timedEvents = dayEvents.filter(event => !isAllDayEvent(event));

                        return (
                            <div key={dayIndex} className="day-column">
                                {timePeriods.map((period, periodIndex) => (
                                    <div
                                        key={periodIndex}
                                        className="time-period-slot"
                                        onClick={() => onCellClick(date, `${period.start.toString().padStart(2, '0')}:00`)}
                                        onDragOver={onDragOver}
                                        onDrop={(e) => onDrop(date, `${period.start.toString().padStart(2, '0')}:00`, e)}
                                    >
                                        {timedEvents
                                            .filter(event => {
                                                const eventHour = new Date(event.StartTime).getHours();
                                                return period.hours.includes(eventHour);
                                            })
                                            .map((event) => {
                                                const family = getFamilyMember(event.FamilyId);
                                                return (
                                                    <div
                                                        key={event.id}
                                                        className="timed-event"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onEventClick(event);
                                                        }}
                                                        draggable
                                                        onDragStart={(e) => onDragStart(event, e)}
                                                        style={{
                                                            background: `linear-gradient(135deg, ${family.color}15 0%, ${family.color}25 100%)`,
                                                            border: `2px solid ${family.color}`
                                                        }}
                                                    >
                                                        <div className="event-subject">{event.Subject}</div>
                                                        <div className="event-time">
                                                            {getTimeString(event.StartTime)} - {getTimeString(event.EndTime)}
                                                        </div>
                                                        <div className="event-family">{family.name}</div>
                                                    </div>
                                                );
                                            })}
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

// Month View Component - Updated for better column alignment
const MonthView = ({ events, currentDate, onEventClick, onCellClick, getFamilyMember, getTimeString }) => {
    // Get month dates
    const getMonthDates = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        // const lastDay = new Date(year, month + 1, 0);
        const startDate = new Date(firstDay);
        startDate.setDate(startDate.getDate() - firstDay.getDay() + 1); // Monday start

        const dates = [];
        const current = new Date(startDate);

        for (let i = 0; i < 42; i++) { // 6 weeks
            dates.push(new Date(current));
            current.setDate(current.getDate() + 1);
        }

        return dates;
    };

    const monthDates = getMonthDates(currentDate);
    // const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    return (
        <div className="month-view">
            <div className="month-header">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                    <div key={day} className="day-header">{day}</div>
                ))}
            </div>

            <div className="month-grid">
                {monthDates.map((date, index) => {
                    const dayEvents = events.filter(event => {
                        const eventStart = new Date(event.StartTime);
                        const eventEnd = new Date(event.EndTime);
                        const dayStart = new Date(date);
                        dayStart.setHours(0, 0, 0, 0);
                        const dayEnd = new Date(date);
                        dayEnd.setHours(23, 59, 59, 999);

                        // Check if event overlaps with this day
                        return (eventStart <= dayEnd && eventEnd >= dayStart);
                    });

                    const isCurrentMonth = date.getMonth() === currentDate.getMonth();
                    const isToday = date.toDateString() === new Date().toDateString();

                    return (
                        <div
                            key={index}
                            className={`month-cell ${isCurrentMonth ? 'current-month' : 'other-month'} ${isToday ? 'today' : ''}`}
                            onClick={() => onCellClick(date)}
                        >
                            <div className="cell-date">{date.getDate()}</div>
                            <div className="cell-events">
                                {dayEvents.slice(0, 3).map((event) => {
                                    const family = getFamilyMember(event.FamilyId);
                                    return (
                                        <div
                                            key={event.id}
                                            className="month-event"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onEventClick(event);
                                            }}
                                            style={{
                                                background: family.color,
                                                color: 'white'
                                            }}
                                        >
                                            <div className="event-subject">{event.Subject}</div>
                                            <div className="event-time">{getTimeString(event.StartTime)}</div>
                                        </div>
                                    );
                                })}
                                {dayEvents.length > 3 && (
                                    <div className="more-events">
                                        +{dayEvents.length - 3} more
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default CustomCalendar;
